/**
 * Read-path hydrant loader for the station board map.
 *
 * Sources fire hydrants exactly the way the LadderApp mobile app does — the
 * "POI-tile pipeline": hydrant positions live as JSON/GeoJSON payloads in
 * Firebase Storage, discovered through Firestore metadata:
 *   - global set:  GlobalPOIFiles collection
 *   - per-agency:  Agencies/{agency}/POIFiles/current
 * Tiles/chunks/single files are fetched by plain fetch(downloadURL) and filtered
 * to a bounding box; hydrants are picked out by type.
 *
 * This mirrors:
 *   - admindashboard/src/components/hydrantManager/globalHydrantImport.js
 *       (fetchGlobalHydrantsInArea — the tiled/chunked/single fetch + dedupe)
 *   - LadderApp/LadderSuite-IOS/utils/SpatialPOILoader.js
 *       (_loadAgencyPOIFiles — agency POI files at Agencies/{agency}/POIFiles/current)
 * ...but it RETURNS the hydrants for rendering and performs NO Firestore writes.
 *
 * Pure tiling / identity / normalization helpers are shared, byte-identical,
 * with the admin dashboard and both mobile apps via ./globalPoiUtils.
 */
import { db, storage } from "../firebase";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import {
  tileKeysForBounds,
  boundsIntersect,
  isUnreliableBounds,
  normalizePoi,
  isHydrantPoi,
  deriveHydrantDocKey,
  isInsideArea,
  polygonsBounds,
  extractPoiArray,
} from "./globalPoiUtils";

// Payloads are immutable, versioned files, so cache aggressively across calls —
// the board re-runs this every time it centers on a new alarm and shouldn't
// re-download the same tiles/metadata.
let _globalFiles = null; // GlobalPOIFiles metadata array, loaded once
const _agencyFiles = new Map(); // agency -> POIFiles/current metadata (or null)
const _payloadCache = new Map(); // downloadURL -> parsed JSON

async function fetchJson(url, cache) {
  if (cache?.has(url)) return cache.get(url);
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  cache?.set(url, json);
  return json;
}

// Resolve a tile's URL: stored downloadURL first, else rebuild from the storage
// path convention poi-tiles/global/v{version}/{z}/{x}/{y}.json.
async function tileUrl(tile, meta) {
  const direct = tile.downloadURL || tile.url;
  if (direct) return direct;
  const base = meta.tileBaseUrl || `poi-tiles/global/v${meta.version}/`;
  const z = tile.zoom ?? String(tile.tileKey || tile.key || "").split("/")[0];
  const x = tile.x ?? String(tile.tileKey || tile.key || "").split("/")[1];
  const y = tile.y ?? String(tile.tileKey || tile.key || "").split("/")[2];
  return getDownloadURL(ref(storage, `${base}${z}/${x}/${y}.json`));
}

/** Load & cache the global POI file metadata (GlobalPOIFiles), once per session. */
async function loadGlobalFiles() {
  if (_globalFiles) return _globalFiles;
  const snap = await getDocs(collection(db, "GlobalPOIFiles"));
  _globalFiles = snap.docs.map((d) => {
    const data = d.data();
    return {
      ...data,
      _source: {
        fileId: d.id,
        fileName: data.originalName || data.filename || d.id,
        version: data.version ?? null,
      },
    };
  });
  return _globalFiles;
}

/**
 * Load & cache one agency's POI file (Agencies/{agency}/POIFiles/current).
 * Mirrors SpatialPOILoader._loadAgencyPOIFiles, including totalPOIs -> poiCount.
 * Returns the metadata object, or null when the agency has no POI file.
 */
async function loadAgencyFile(agency) {
  if (_agencyFiles.has(agency)) return _agencyFiles.get(agency);
  let meta = null;
  try {
    const d = await getDoc(doc(db, "Agencies", agency, "POIFiles", "current"));
    if (d.exists()) {
      const data = d.data();
      meta = {
        ...data,
        poiCount: data.totalPOIs ?? data.poiCount,
        _source: {
          fileId: `agency_${agency}`,
          fileName: agency,
          version: data.version ?? null,
        },
      };
    }
  } catch (err) {
    console.error(`[HydrantLoader] agency POI file load failed for ${agency}:`, err);
  }
  _agencyFiles.set(agency, meta);
  return meta;
}

/**
 * Fetch all hydrant POIs inside `area` from every global + agency source file.
 * The area's envelope selects which tiles/chunks/files to download; an
 * inside-area check then filters exactly to the drawn box/polygon.
 *
 * @param {{ bounds?: {north,south,east,west}, polygons?: Array }} area
 * @param {{ agencies?: string[], onProgress?: (msg:string)=>void }} [opts]
 * @returns {Promise<{ pois: Array<{poi:object, source:object}>, stats:object }>}
 *          deduped by hydrant doc key (deriveHydrantDocKey)
 */
export async function loadHydrantsForArea(area, { agencies = [], onProgress = () => {} } = {}) {
  const stats = {
    files: 0,
    filesInArea: 0,
    payloadsFetched: 0,
    fetchErrors: 0,
    poisScanned: 0,
    hydrantsFound: 0,
    noId: 0,
  };
  const byId = new Map();

  const bounds = area?.bounds || polygonsBounds(area?.polygons);
  if (!bounds) return { pois: [], stats };

  // Assemble the metadata files to scan: global set + each agency's POI file.
  let metaFiles = [];
  try {
    metaFiles = [...(await loadGlobalFiles())];
  } catch (err) {
    console.error("[HydrantLoader] GlobalPOIFiles load failed:", err);
  }
  const uniqueAgencies = [...new Set((agencies || []).filter(Boolean))];
  const agencyMetas = await Promise.all(uniqueAgencies.map((a) => loadAgencyFile(a)));
  metaFiles = metaFiles.concat(agencyMetas.filter(Boolean));
  stats.files = metaFiles.length;

  const ingest = (rawPois, source) => {
    for (const raw of rawPois) {
      stats.poisScanned += 1;
      const poi = normalizePoi(raw);
      if (!poi || !isHydrantPoi(poi) || !isInsideArea(poi, { ...area, bounds })) continue;
      stats.hydrantsFound += 1;
      // Key on the hydrant doc key (coordinate-based when the source has no real
      // ID) so distinct hydrants with placeholder IDs don't collapse, and so the
      // key lines up with the docs the admin importer writes.
      const docId = deriveHydrantDocKey(poi);
      if (!docId) {
        stats.noId += 1;
        continue;
      }
      if (!byId.has(docId)) byId.set(docId, { poi, source });
    }
  };

  for (const meta of metaFiles) {
    const source = meta._source || { fileId: meta.id || "", fileName: "", version: meta.version ?? null };
    if (!boundsIntersect(meta.bounds, bounds)) continue;
    stats.filesInArea += 1;

    try {
      if (meta.processingStrategy === "tiled" && Array.isArray(meta.tiles)) {
        // Use the lowest zoom present — fewest tiles, full coverage (the uploader
        // duplicates data across zoom levels).
        const zooms = [
          ...new Set(
            meta.tiles.map(
              (t) => t.zoom ?? parseInt(String(t.tileKey || t.key || "").split("/")[0], 10)
            )
          ),
        ].filter(Number.isFinite);
        const zoom = Math.min(...zooms);
        const atZoom = meta.tiles.filter((t) => {
          const tz = t.zoom ?? parseInt(String(t.tileKey || t.key || "").split("/")[0], 10);
          return tz === zoom;
        });
        // The uploader computes tile placement from latitude/lat fields only;
        // GeoJSON/WKT/Shape_X-sourced payloads land in the 0/0 tile with all-zero
        // bounds. When the file's bounds are unreliable, tile keys can't be
        // trusted either — fetch every tile at the zoom and let ingest() filter
        // by the real (normalized) coordinates.
        let tiles;
        if (isUnreliableBounds(meta.bounds)) {
          tiles = atZoom;
        } else {
          const wanted = new Set(tileKeysForBounds(bounds, zoom));
          tiles = atZoom.filter((t) => {
            const key = t.tileKey || t.key || `${t.zoom}/${t.x}/${t.y}`;
            return wanted.has(key);
          });
          // Reliable bounds intersected the area, yet none of this file's tiles
          // matched — its tiles were placed inconsistently with its bounds. Don't
          // silently drop it; scan every tile and let the inside-area check filter.
          if (tiles.length === 0 && atZoom.length > 0) tiles = atZoom;
        }
        onProgress(`Fetching ${tiles.length} tile(s) from ${source.fileName}…`);
        for (const tile of tiles) {
          try {
            const json = await fetchJson(await tileUrl(tile, meta), _payloadCache);
            stats.payloadsFetched += 1;
            ingest(extractPoiArray(json), source);
          } catch (err) {
            console.error("[HydrantLoader] tile fetch failed", tile, err);
            stats.fetchErrors += 1;
          }
        }
      } else if (meta.processingStrategy === "chunked" && Array.isArray(meta.chunks)) {
        const chunks = meta.chunks.filter((c) => boundsIntersect(c.bounds, bounds));
        onProgress(`Fetching ${chunks.length} chunk(s) from ${source.fileName}…`);
        for (const chunk of chunks) {
          try {
            const json = await fetchJson(chunk.downloadURL, _payloadCache);
            stats.payloadsFetched += 1;
            ingest(extractPoiArray(json), source);
          } catch (err) {
            console.error("[HydrantLoader] chunk fetch failed", chunk, err);
            stats.fetchErrors += 1;
          }
        }
      } else if (meta.downloadURL) {
        onProgress(`Fetching ${source.fileName}…`);
        const json = await fetchJson(meta.downloadURL, _payloadCache);
        stats.payloadsFetched += 1;
        ingest(extractPoiArray(json), source);
      }
    } catch (err) {
      console.error("[HydrantLoader] POI file failed", source.fileId, err);
      stats.fetchErrors += 1;
    }
  }

  console.log("[HydrantLoader]", { ...stats, returned: byId.size });
  return { pois: [...byId.values()], stats };
}

/** Clear all caches (diagnostics / tests). */
export function clearHydrantCaches() {
  _globalFiles = null;
  _agencyFiles.clear();
  _payloadCache.clear();
}
